import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { getDatabaseUnderstandings, updateDatabaseUnderstanding, regenerateDatabaseUnderstanding } from '../services/api';
import { Brain, Database, AlertCircle, Trash2, Pencil, X, Plus, Search } from 'lucide-react';
import { SectionCard } from '../components/ui/SectionCard';

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
};

const EditableField = ({ value, onChange, placeholder, multiline = false, className = "" }) => {
    if (multiline) {
        return (
            <textarea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`w-full px-3 py-2 border border-gray-300 rounded text-sm ${className}`}
                rows={2}
            />
        );
    }
    return (
        <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full px-3 py-2 border border-gray-300 rounded text-sm ${className}`}
        />
    );
};

const BusinessRuleItem = ({ rule, index, onSave, onDelete }) => {
    const { t } = useTranslation(['page-db-understanding-settings', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...rule });

    if (!rule) return null;

    const handleSave = () => {
        onSave(index, formData);
        setIsEditing(false);
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (isEditing) {
        return (
            <div className="p-3 bg-white border border-blue-300 rounded-lg mb-2 space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:businessRuleItem.nameLabel')}</label>
                    <EditableField
                        value={formData.name}
                        onChange={(v) => updateField('name', v)}
                        placeholder={t('page-db-understanding-settings:businessRuleItem.namePlaceholder')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:businessRuleItem.descriptionLabel')}</label>
                    <EditableField
                        value={formData.description}
                        onChange={(v) => updateField('description', v)}
                        placeholder={t('page-db-understanding-settings:businessRuleItem.descriptionPlaceholder')}
                        multiline
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:businessRuleItem.filterLabel')}</label>
                    <EditableField
                        value={formData.filter}
                        onChange={(v) => updateField('filter', v)}
                        placeholder={t('page-db-understanding-settings:businessRuleItem.filterPlaceholder')}
                        className="font-mono"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:businessRuleItem.appliesToLabel')}</label>
                    <EditableField
                        value={(formData.applies_to || []).join(', ')}
                        onChange={(v) => updateField('applies_to', v.split(',').map(s => s.trim()).filter(Boolean))}
                        placeholder={t('page-db-understanding-settings:businessRuleItem.appliesToPlaceholder')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:businessRuleItem.reasonLabel')}</label>
                    <EditableField
                        value={formData.reason || ''}
                        onChange={(v) => updateField('reason', v)}
                        placeholder={t('page-db-understanding-settings:businessRuleItem.reasonPlaceholder')}
                    />
                </div>
                <div className="flex gap-2 pt-2">
                    <button onClick={handleSave} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">{t('common:save')}</button>
                    <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50">{t('common:cancel')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 bg-white border border-gray-200 rounded-lg mb-2">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="font-medium text-gray-900">{rule.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{rule.description}</div>

                    <div className="mt-3">
                        <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:businessRuleItem.filterColon')}</span>
                        <div className="mt-1 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-xs font-mono text-yellow-800">
                            {rule.filter}
                        </div>
                    </div>

                    {rule.applies_to && rule.applies_to.length > 0 && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:businessRuleItem.appliesToColon')}</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {rule.applies_to.map((val, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{val}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {rule.reason && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:businessRuleItem.reasonColon')}</span>
                            <div className="text-sm text-gray-600">{rule.reason}</div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => setIsEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const EntityFilterItem = ({ entityFilter, index, onSave, onDelete }) => {
    const { t } = useTranslation(['page-db-understanding-settings', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...entityFilter });

    if (!entityFilter) return null;

    const handleSave = () => {
        onSave(index, formData);
        setIsEditing(false);
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (isEditing) {
        return (
            <div className="p-3 bg-white border border-blue-300 rounded-lg mb-2 space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:entityFilterItem.entityNameLabel')}</label>
                    <EditableField
                        value={formData.entity}
                        onChange={(v) => updateField('entity', v)}
                        placeholder={t('page-db-understanding-settings:entityFilterItem.entityNamePlaceholder')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:entityFilterItem.descriptionLabel')}</label>
                    <EditableField
                        value={formData.description}
                        onChange={(v) => updateField('description', v)}
                        placeholder={t('page-db-understanding-settings:entityFilterItem.descriptionPlaceholder')}
                        multiline
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:entityFilterItem.filterLabel')}</label>
                    <EditableField
                        value={formData.filter}
                        onChange={(v) => updateField('filter', v)}
                        placeholder={t('page-db-understanding-settings:entityFilterItem.filterPlaceholder')}
                        className="font-mono"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:entityFilterItem.alternativesLabel')}</label>
                    <EditableField
                        value={(formData.alternatives || []).join(', ')}
                        onChange={(v) => updateField('alternatives', v.split(',').map(s => s.trim()).filter(Boolean))}
                        placeholder={t('page-db-understanding-settings:entityFilterItem.alternativesPlaceholder')}
                    />
                </div>
                <div className="flex gap-2 pt-2">
                    <button onClick={handleSave} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">{t('common:save')}</button>
                    <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50">{t('common:cancel')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 bg-white border border-gray-200 rounded-lg mb-2">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="font-medium text-gray-900">{entityFilter.entity}</div>
                    <div className="text-sm text-gray-600 mt-1">{entityFilter.description}</div>

                    <div className="mt-3">
                        <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:entityFilterItem.filterColon')}</span>
                        <div className="mt-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs font-mono text-green-800">
                            {entityFilter.filter}
                        </div>
                    </div>
                    {entityFilter.alternatives && entityFilter.alternatives.length > 0 && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:entityFilterItem.alternativesColon')}</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {entityFilter.alternatives.map((val, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">{val}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => setIsEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const ColumnPurposeItem = ({ purpose, tableName, tableIndex, columnIndex, onSave, onDelete }) => {
    const { t } = useTranslation(['page-db-understanding-settings', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...purpose });

    if (!purpose) return null;

    const handleSave = () => {
        onSave(tableIndex, columnIndex, formData);
        setIsEditing(false);
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (isEditing) {
        return (
            <div className="p-3 bg-white border border-blue-300 rounded-lg space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:columnPurposeItem.columnNameLabel')}</label>
                    <EditableField
                        value={formData.name}
                        onChange={(v) => updateField('name', v)}
                        placeholder={t('page-db-understanding-settings:columnPurposeItem.columnNamePlaceholder')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:columnPurposeItem.businessNameLabel')}</label>
                    <EditableField
                        value={formData.business_name}
                        onChange={(v) => updateField('business_name', v)}
                        placeholder={t('page-db-understanding-settings:columnPurposeItem.businessNamePlaceholder')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:columnPurposeItem.descriptionLabel')}</label>
                    <EditableField
                        value={formData.description}
                        onChange={(v) => updateField('description', v)}
                        placeholder={t('page-db-understanding-settings:columnPurposeItem.descriptionPlaceholder')}
                        multiline
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:columnPurposeItem.validValuesLabel')}</label>
                    <EditableField
                        value={(formData.valid_values || []).join(', ')}
                        onChange={(v) => updateField('valid_values', v ? v.split(',').map(s => s.trim()) : [])}
                        placeholder={t('page-db-understanding-settings:columnPurposeItem.validValuesPlaceholder')}
                    />
                </div>
                {formData.related_to && (
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:columnPurposeItem.relatedToLabel')}</label>
                        <div className="text-xs text-gray-500 mb-1">{formData.related_to.table}.{formData.related_to.column}</div>
                        <EditableField
                            value={formData.related_to.description}
                            onChange={(v) => updateField('related_to', { ...formData.related_to, description: v })}
                            placeholder={t('page-db-understanding-settings:columnPurposeItem.relatedToDescriptionPlaceholder')}
                            multiline
                        />
                    </div>
                )}
                <div className="flex gap-2 pt-2">
                    <button onClick={handleSave} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">{t('common:save')}</button>
                    <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50">{t('common:cancel')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{purpose.business_name || purpose.name}</span>
                        <span className="text-xs text-gray-500">{tableName}.{purpose.name}</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{purpose.description}</div>
                    {purpose.valid_values && purpose.valid_values.length > 0 && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:columnPurposeItem.validValuesColon')}</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {purpose.valid_values.map((val, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">{val}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {purpose.related_to && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:columnPurposeItem.relatedToColon')}</span>
                            <div className="mt-1 flex items-start gap-2">
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-mono">
                                    {purpose.related_to.table}.{purpose.related_to.column}
                                </span>
                                <span className="text-xs text-gray-600">{purpose.related_to.description}</span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => setIsEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(tableIndex, columnIndex)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const MetricItem = ({ metric, index, onSave, onDelete }) => {
    const { t } = useTranslation(['page-db-understanding-settings', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...metric });

    if (!metric) return null;

    const handleSave = () => {
        onSave(index, formData);
        setIsEditing(false);
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (isEditing) {
        return (
            <div className="p-3 bg-white border border-blue-300 rounded-lg mb-2 space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:metricItem.metricNameLabel')}</label>
                    <EditableField
                        value={formData.metric}
                        onChange={(v) => updateField('metric', v)}
                        placeholder={t('page-db-understanding-settings:metricItem.metricNamePlaceholder')}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:metricItem.descriptionLabel')}</label>
                    <EditableField
                        value={formData.description}
                        onChange={(v) => updateField('description', v)}
                        placeholder={t('page-db-understanding-settings:metricItem.descriptionPlaceholder')}
                        multiline
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:metricItem.calculationLabel')}</label>
                    <EditableField
                        value={formData.calculation}
                        onChange={(v) => updateField('calculation', v)}
                        placeholder={t('page-db-understanding-settings:metricItem.calculationPlaceholder')}
                        className="font-mono"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('page-db-understanding-settings:metricItem.filtersLabel')}</label>
                    <EditableField
                        value={(formData.filters || []).join(', ')}
                        onChange={(v) => updateField('filters', v.split(',').map(s => s.trim()))}
                        placeholder={t('page-db-understanding-settings:metricItem.filtersPlaceholder')}
                    />
                </div>
                <div className="flex gap-2 pt-2">
                    <button onClick={handleSave} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">{t('common:save')}</button>
                    <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50">{t('common:cancel')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 bg-white border border-gray-200 rounded-lg mb-2">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="font-medium text-gray-900">{metric.metric}</div>
                    <div className="text-sm text-gray-600 mt-1">{metric.description}</div>

                    <div className="mt-3">
                        <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:metricItem.calculationColon')}</span>
                        <div className="mt-1 px-2 py-1 bg-orange-50 border border-orange-200 rounded text-xs font-mono text-orange-800">
                            {metric.calculation}
                        </div>
                    </div>

                    {metric.filters && metric.filters.length > 0 && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">{t('page-db-understanding-settings:metricItem.filtersColon')}</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {metric.filters.map((val, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">{val}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => setIsEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const DataAssumptionItem = ({ assumption, index, onSave, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...assumption });

    if (!assumption) return null;

    const handleSave = () => {
        onSave(index, formData);
        setIsEditing(false);
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (isEditing) {
        return (
            <div className="p-3 bg-white border border-blue-300 rounded-lg mb-2 space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Table Name</label>
                    <EditableField
                        value={formData.table}
                        onChange={(v) => updateField('table', v)}
                        placeholder="e.g., orders"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Column Name</label>
                    <EditableField
                        value={formData.column}
                        onChange={(v) => updateField('column', v)}
                        placeholder="e.g., total_amount"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Assumption</label>
                    <EditableField
                        value={formData.assumption || ''}
                        onChange={(v) => updateField('assumption', v)}
                        placeholder="e.g., Always positive, decimal with 2 precision"
                        multiline
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Null Handling</label>
                    <EditableField
                        value={formData.null_handling || ''}
                        onChange={(v) => updateField('null_handling', v)}
                        placeholder="e.g., Treat NULL as 0"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Validation</label>
                    <EditableField
                        value={formData.validation || ''}
                        onChange={(v) => updateField('validation', v)}
                        placeholder="e.g., Must be >= 0 for completed orders"
                    />
                </div>
                <div className="flex gap-2 pt-2">
                    <button onClick={handleSave} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Save</button>
                    <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50">Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 bg-white border border-gray-200 rounded-lg mb-2">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{assumption.table}.{assumption.column}</span>
                    </div>
                    {assumption.assumption && (
                        <div className="text-sm text-gray-600 mt-1">{assumption.assumption}</div>
                    )}
                    {assumption.null_handling && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">Null Handling:</span>
                            <div className="text-sm text-gray-600">{assumption.null_handling}</div>
                        </div>
                    )}
                    {assumption.validation && (
                        <div className="mt-3">
                            <span className="text-xs font-medium text-gray-500">Validation:</span>
                            <div className="text-sm text-gray-600">{assumption.validation}</div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => setIsEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export const DatabaseUnderstandingSettings = () => {
    const { databases } = useDatabase();
    const { showToast } = useToast();
    const [selectedDb, setSelectedDb] = useState(null);
    const [selectedUnderstanding, setSelectedUnderstanding] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [addingType, setAddingType] = useState(null);
    const [newItemForm, setNewItemForm] = useState({});

    useEffect(() => {
        if (selectedDb) {
            fetchUnderstandings();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDb]);

    const fetchUnderstandings = async () => {
        if (!selectedDb) return;
        setLoading(true);
        try {
            const response = await getDatabaseUnderstandings(selectedDb.id);
            const data = response.data?.data?.understandings || [];
            if (data.length > 0) {
                setSelectedUnderstanding(data[0]);
            } else {
                setSelectedUnderstanding(null);
            }
        } catch (error) {
            showToast('Failed to fetch database understanding', 'error');
        } finally {
            setLoading(false);
        }
    };

    const saveUnderstanding = async (updatedJson) => {
        if (!selectedDb || !selectedUnderstanding) return;
        try {
            await updateDatabaseUnderstanding(selectedDb.id, selectedUnderstanding.id, { understanding_json: updatedJson });
            setSelectedUnderstanding({ ...selectedUnderstanding, understanding_json: updatedJson });
            showToast('Saved successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.detail || 'Failed to save', 'error');
        }
    };

    const handleUpdateItem = (arrayKey, index, updatedItem) => {
        if (!selectedUnderstanding) return;
        const currentJson = { ...selectedUnderstanding.understanding_json };
        const arr = currentJson[arrayKey] || [];
        arr[index] = updatedItem;
        currentJson[arrayKey] = arr;
        saveUnderstanding(currentJson);
    };

    const handleDeleteItem = (arrayKey, index) => {
        if (!selectedUnderstanding) return;
        const currentJson = { ...selectedUnderstanding.understanding_json };
        const arr = currentJson[arrayKey] || [];
        arr.splice(index, 1);
        currentJson[arrayKey] = arr;
        saveUnderstanding(currentJson);
    };

    const handleAddItem = (arrayKey) => {
        if (!selectedUnderstanding) return;
        setAddingType(arrayKey);
        
        if (arrayKey === 'business_rules') {
            setNewItemForm({ name: '', description: '', filter: '', applies_to: '', reason: '' });
        } else if (arrayKey === 'entity_filters') {
            setNewItemForm({ entity: '', description: '', filter: '', alternatives: '' });
        } else if (arrayKey === 'column_purposes') {
            setNewItemForm({ table: '', column: '', business_name: '', description: '', valid_values: '' });
        } else if (arrayKey === 'metric_definitions') {
            setNewItemForm({ metric: '', description: '', calculation: '', filters: '' });
        } else if (arrayKey === 'data_assumptions') {
            setNewItemForm({ table: '', column: '', assumption: '', null_handling: '', validation: '' });
        }
        
        setShowAddModal(true);
    };

    const saveNewItem = () => {
        if (!selectedUnderstanding) return;
        const currentJson = { ...selectedUnderstanding.understanding_json };
        
        if (addingType === 'column_purposes') {
            const arr = currentJson.column_purposes || [];
            const tableIdx = arr.findIndex(t => t.table === newItemForm.table);
            const newCol = {
                name: newItemForm.column,
                business_name: newItemForm.business_name,
                description: newItemForm.description,
                valid_values: newItemForm.valid_values ? newItemForm.valid_values.split(',').map(s => s.trim()).filter(Boolean) : []
            };

            if (tableIdx > -1) {
                arr[tableIdx].columns = [...(arr[tableIdx].columns || []), newCol];
            } else {
                arr.push({ table: newItemForm.table, columns: [newCol] });
            }
            currentJson.column_purposes = arr;
        } else {
            let newItem;
            if (addingType === 'business_rules') {
                newItem = {
                    name: newItemForm.name,
                    description: newItemForm.description,
                    filter: newItemForm.filter,
                    applies_to: newItemForm.applies_to ? newItemForm.applies_to.split(',').map(s => s.trim()).filter(Boolean) : [],
                    reason: newItemForm.reason
                };
            } else if (addingType === 'entity_filters') {
                newItem = {
                    entity: newItemForm.entity,
                    description: newItemForm.description,
                    filter: newItemForm.filter,
                    alternatives: newItemForm.alternatives ? newItemForm.alternatives.split(',').map(s => s.trim()).filter(Boolean) : []
                };
            } else if (addingType === 'metric_definitions') {
                newItem = {
                    metric: newItemForm.metric,
                    description: newItemForm.description,
                    calculation: newItemForm.calculation,
                    filters: newItemForm.filters ? newItemForm.filters.split(',').map(s => s.trim()).filter(Boolean) : []
                };
            } else if (addingType === 'data_assumptions') {
                newItem = {
                    table: newItemForm.table,
                    column: newItemForm.column,
                    assumption: newItemForm.assumption,
                    null_handling: newItemForm.null_handling,
                    validation: newItemForm.validation
                };
            }
            
            if (newItem) {
                const arr = currentJson[addingType] || [];
                arr.push(newItem);
                currentJson[addingType] = arr;
            }
        }
        
        saveUnderstanding(currentJson);
        setShowAddModal(false);
    };

    const u = selectedUnderstanding?.understanding_json || {};
    const businessRules = u.business_rules || [];
    const entityFilters = u.entity_filters || [];
    const columnPurposes = u.column_purposes || [];
    const dataAssumptions = u.data_assumptions || [];
    const metricDefinitions = u.metric_definitions || [];
    const domainInsights = u.domain_insights || [];

    const filteredDatabases = databases.filter((db) =>
        db.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex gap-theme">
            {/* Left Sidebar — match Schema Analysis */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col rounded-xl shadow-sm h-fit max-h-[calc(100vh-8rem)]">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900 mb-1">Select Database</h2>
                </div>

                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search databases..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {filteredDatabases.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                            {searchQuery ? 'No matching databases' : 'No databases connected'}
                        </p>
                    ) : (
                        filteredDatabases.map((db) => (
                            <div
                                key={db.id}
                                onClick={() => {
                                    setSelectedDb(db);
                                    setSelectedUnderstanding(null);
                                }}
                                className={`p-3 rounded-lg border transition-all cursor-pointer group ${selectedDb?.id === db.id
                                    ? 'bg-primary-50 border-primary-300'
                                    : 'bg-white border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedDb?.id === db.id ? 'bg-primary-100' : 'bg-green-100'
                                        }`}>
                                        <Database className={`w-4 h-4 ${selectedDb?.id === db.id ? 'text-primary-600' : 'text-green-600'
                                            }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-900 text-sm truncate">
                                                {db.name}
                                            </span>
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${selectedDb?.id === db.id
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                {db.status || 'Analyzed'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{db.db_type || 'PostgreSQL'}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {selectedUnderstanding && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                        <p className="text-xs text-gray-500">
                            Updated: {selectedUnderstanding.updated_at
                                ? new Date(selectedUnderstanding.updated_at).toLocaleString()
                                : 'N/A'}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex-1 page-content min-w-0">
                {!selectedDb ? (
                    <div className="bg-white rounded-lg border border-gray-200 text-center p-8">
                        <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Database Selected</h3>
                        <p className="text-gray-600">Please select a database from the left sidebar to view and edit its understanding</p>
                    </div>
                ) : (
                    <div>
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h1 className="page-title flex items-center gap-2">
                                    <Brain className="w-6 h-6 text-primary-600" />
                                    Database Understanding
                                </h1>
                                <p className="page-subtitle">Edit business rules, entity filters, column purposes, and metric definitions</p>
                            </div>
                            <button
                                onClick={async () => {
                                    try {
                                        await regenerateDatabaseUnderstanding(selectedDb.id);
                                        showToast('Understanding regenerated successfully', 'success');
                                        const updated = await getDatabaseUnderstandings(selectedDb.id);
                                        setSelectedUnderstanding(updated.data[0]);
                                    } catch (error) {
                                        showToast(error.response?.data?.detail || 'Failed to regenerate', 'error');
                                    }
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                            >
                                Generate Understanding
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : !selectedUnderstanding ? (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-yellow-600" />
                                <div>
                                    <p className="font-medium text-yellow-800">No understanding yet</p>
                                    <p className="text-yellow-700 text-sm">Run schema analysis to generate understanding automatically.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {domainInsights.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-blue-900 mb-2">Domain Insights</h3>
                                        <ul className="space-y-1">
                                            {domainInsights.filter(Boolean).map((insight, idx) => (
                                                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                                                    <span className="text-blue-400">•</span>
                                                    {insight}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <SectionCard title="Business Rules" count={businessRules.length} defaultOpen={true}>
                                    <div className="flex justify-end mb-2">
                                        <button onClick={() => handleAddItem('business_rules')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                            <Plus className="w-3 h-3" /> Add Rule
                                        </button>
                                    </div>
                                    {businessRules.length === 0 ? (
                                        <p className="text-sm text-gray-500">No business rules defined. Click "Add Rule" to create one.</p>
                                    ) : (
                                        businessRules.filter(Boolean).map((rule, idx) => (
                                            <BusinessRuleItem
                                                key={idx}
                                                rule={rule}
                                                index={idx}
                                                onSave={(i, item) => handleUpdateItem('business_rules', i, item)}
                                                onDelete={(i) => handleDeleteItem('business_rules', i)}
                                            />
                                        ))
                                    )}
                                </SectionCard>

                                <SectionCard title="Entity Filters" count={entityFilters.length} defaultOpen={true}>
                                    <div className="flex justify-end mb-2">
                                        <button onClick={() => handleAddItem('entity_filters')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                            <Plus className="w-3 h-3" /> Add Filter
                                        </button>
                                    </div>
                                    {entityFilters.length === 0 ? (
                                        <p className="text-sm text-gray-500">No entity filters defined. Click "Add Filter" to create one.</p>
                                    ) : (
                                        entityFilters.filter(Boolean).map((ef, idx) => (
                                            <EntityFilterItem
                                                key={idx}
                                                entityFilter={ef}
                                                index={idx}
                                                onSave={(i, item) => handleUpdateItem('entity_filters', i, item)}
                                                onDelete={(i) => handleDeleteItem('entity_filters', i)}
                                            />
                                        ))
                                    )}
                                </SectionCard>

                                <SectionCard title="Column Purposes" count={columnPurposes.length} defaultOpen={false}>
                                    {columnPurposes.length === 0 ? (
                                        <p className="text-sm text-gray-500">No column purposes defined. These are auto-generated during schema analysis.</p>
                                    ) : (
                                        columnPurposes.filter(Boolean).map((tableEntry, tableIdx) => (
                                            <div key={tableIdx} className="mb-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Database className="w-4 h-4 text-gray-500" />
                                                        <h3 className="font-semibold text-gray-900">{tableEntry.table}</h3>
                                                    </div>
                                                    <span className="text-xs text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                                                        {(tableEntry.columns || []).length} columns
                                                    </span>
                                                </div>
                                                <div className="p-4 bg-gray-50 space-y-3">
                                                    {(tableEntry.columns || []).map((col, colIdx) => (
                                                        <ColumnPurposeItem
                                                            key={`${tableIdx}-${colIdx}`}
                                                            purpose={col}
                                                            tableName={tableEntry.table}
                                                            tableIndex={tableIdx}
                                                            columnIndex={colIdx}
                                                            onSave={(tIdx, cIdx, item) => {
                                                                const updated = [...columnPurposes];
                                                                updated[tIdx].columns[cIdx] = item;
                                                                handleUpdateItem('column_purposes', tIdx, updated[tIdx]);
                                                            }}
                                                            onDelete={(tIdx, cIdx) => {
                                                                const updated = [...columnPurposes];
                                                                updated[tIdx].columns.splice(cIdx, 1);
                                                                if (updated[tIdx].columns.length === 0) {
                                                                    updated.splice(tIdx, 1);
                                                                }
                                                                handleUpdateItem('column_purposes', tIdx, updated[tIdx]);
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </SectionCard>

                                <SectionCard title="Data Assumptions" count={dataAssumptions.length} defaultOpen={false}>
                                    <div className="flex justify-end mb-2">
                                        <button onClick={() => handleAddItem('data_assumptions')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                            <Plus className="w-3 h-3" /> Add Assumption
                                        </button>
                                    </div>
                                    {dataAssumptions.length === 0 ? (
                                        <p className="text-sm text-gray-500">No data assumptions defined. Click "Add Assumption" to create one.</p>
                                    ) : (
                                        dataAssumptions.filter(Boolean).map((assumption, idx) => (
                                            <DataAssumptionItem
                                                key={idx}
                                                assumption={assumption}
                                                index={idx}
                                                onSave={(i, item) => handleUpdateItem('data_assumptions', i, item)}
                                                onDelete={(i) => handleDeleteItem('data_assumptions', i)}
                                            />
                                        ))
                                    )}
                                </SectionCard>

                                <SectionCard title="Metric Definitions" count={metricDefinitions.length} defaultOpen={false}>
                                    <div className="flex justify-end mb-2">
                                        <button onClick={() => handleAddItem('metric_definitions')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                            <Plus className="w-3 h-3" /> Add Metric
                                        </button>
                                    </div>
                                    {metricDefinitions.length === 0 ? (
                                        <p className="text-sm text-gray-500">No metric definitions defined. Click "Add Metric" to create one.</p>
                                    ) : (
                                        metricDefinitions.filter(Boolean).map((m, idx) => (
                                            <MetricItem
                                                key={idx}
                                                metric={m}
                                                index={idx}
                                                onSave={(i, item) => handleUpdateItem('metric_definitions', i, item)}
                                                onDelete={(i) => handleDeleteItem('metric_definitions', i)}
                                            />
                                        ))
                                    )}
                                </SectionCard>

                                <div className="text-sm text-gray-500 text-right pt-4">
                                    Last updated: {selectedUnderstanding.updated_at ? new Date(selectedUnderstanding.updated_at).toLocaleString() : 'N/A'}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={
            addingType === 'business_rules' ? 'Add Business Rule' :
            addingType === 'entity_filters' ? 'Add Entity Filter' :
            addingType === 'column_purposes' ? 'Add Column Purpose' :
            addingType === 'metric_definitions' ? 'Add Metric Definition' :
            addingType === 'data_assumptions' ? 'Add Data Assumption' : 'Add New Item'
        }>
            {addingType === 'business_rules' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input type="text" value={newItemForm.name || ''} onChange={(e) => setNewItemForm({...newItemForm, name: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., Exclude cancelled orders" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea value={newItemForm.description || ''} onChange={(e) => setNewItemForm({...newItemForm, description: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} placeholder="e.g., Ignore orders with status cancelled or returned" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter (SQL WHERE)</label>
                        <input type="text" value={newItemForm.filter || ''} onChange={(e) => setNewItemForm({...newItemForm, filter: e.target.value})} className="w-full border rounded px-3 py-2 font-mono text-sm" placeholder="e.g., status NOT IN ('cancelled', 'returned')" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Applies To (comma separated)</label>
                        <input type="text" value={newItemForm.applies_to || ''} onChange={(e) => setNewItemForm({...newItemForm, applies_to: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., total_revenue, order_count" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                        <input type="text" value={newItemForm.reason || ''} onChange={(e) => setNewItemForm({...newItemForm, reason: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., These represent uncompleted transactions" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                        <button onClick={saveNewItem} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                    </div>
                </div>
            )}
            {addingType === 'entity_filters' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name *</label>
                        <input type="text" value={newItemForm.entity || ''} onChange={(e) => setNewItemForm({...newItemForm, entity: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., restaurant" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea value={newItemForm.description || ''} onChange={(e) => setNewItemForm({...newItemForm, description: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} placeholder="e.g., How to identify restaurants" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter (SQL WHERE)</label>
                        <input type="text" value={newItemForm.filter || ''} onChange={(e) => setNewItemForm({...newItemForm, filter: e.target.value})} className="w-full border rounded px-3 py-2 font-mono text-sm" placeholder="e.g., parent_id > 0 AND restaurant = true" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Alternatives (comma separated)</label>
                        <input type="text" value={newItemForm.alternatives || ''} onChange={(e) => setNewItemForm({...newItemForm, alternatives: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., type = 'restaurant'" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                        <button onClick={saveNewItem} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                    </div>
                </div>
            )}
            {addingType === 'column_purposes' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Table Name *</label>
                        <input type="text" value={newItemForm.table || ''} onChange={(e) => setNewItemForm({...newItemForm, table: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., orders" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Column Name *</label>
                        <input type="text" value={newItemForm.column || ''} onChange={(e) => setNewItemForm({...newItemForm, column: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., status" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                        <input type="text" value={newItemForm.business_name || ''} onChange={(e) => setNewItemForm({...newItemForm, business_name: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., Order Status" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea value={newItemForm.description || ''} onChange={(e) => setNewItemForm({...newItemForm, description: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} placeholder="e.g., Current state of the order in its lifecycle" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Valid Values (comma separated)</label>
                        <input type="text" value={newItemForm.valid_values || ''} onChange={(e) => setNewItemForm({...newItemForm, valid_values: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., pending, processing, completed, cancelled" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter Usage</label>
                        <input type="text" value={newItemForm.filter_usage || ''} onChange={(e) => setNewItemForm({...newItemForm, filter_usage: e.target.value})} className="w-full border rounded px-3 py-2 font-mono text-sm" placeholder="e.g., Use NOT IN ('cancelled', 'returned')" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                        <button onClick={saveNewItem} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                    </div>
                </div>
            )}
            {addingType === 'metric_definitions' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Metric Name *</label>
                        <input type="text" value={newItemForm.metric || ''} onChange={(e) => setNewItemForm({...newItemForm, metric: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., total_revenue" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea value={newItemForm.description || ''} onChange={(e) => setNewItemForm({...newItemForm, description: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} placeholder="e.g., Total revenue from completed orders" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Calculation (SQL)</label>
                        <input type="text" value={newItemForm.calculation || ''} onChange={(e) => setNewItemForm({...newItemForm, calculation: e.target.value})} className="w-full border rounded px-3 py-2 font-mono text-sm" placeholder="e.g., SUM(total_amount) WHERE status = 'completed'" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filters (comma separated)</label>
                        <input type="text" value={newItemForm.filters || ''} onChange={(e) => setNewItemForm({...newItemForm, filters: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., status NOT IN ('cancelled', 'pending')" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                        <button onClick={saveNewItem} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                    </div>
                </div>
            )}
            {addingType === 'data_assumptions' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Table Name *</label>
                        <input type="text" value={newItemForm.table || ''} onChange={(e) => setNewItemForm({...newItemForm, table: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., orders" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Column Name *</label>
                        <input type="text" value={newItemForm.column || ''} onChange={(e) => setNewItemForm({...newItemForm, column: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., total_amount" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assumption</label>
                        <textarea value={newItemForm.assumption || ''} onChange={(e) => setNewItemForm({...newItemForm, assumption: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} placeholder="e.g., Always positive, decimal with 2 precision" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Null Handling</label>
                        <input type="text" value={newItemForm.null_handling || ''} onChange={(e) => setNewItemForm({...newItemForm, null_handling: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., 0 means no amount recorded" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Validation</label>
                        <input type="text" value={newItemForm.validation || ''} onChange={(e) => setNewItemForm({...newItemForm, validation: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="e.g., Should be > 0 for completed orders" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                        <button onClick={saveNewItem} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                    </div>
                </div>
            )}
            </Modal>
        </div>
    );
};

export default DatabaseUnderstandingSettings;